import React, { useState, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';

// Layers
import TrafficSensorLayer from './layers/TrafficSensorLayer';
import CompetitorLayer from './layers/CompetitorLayer';
import AlcoholZoneLayer from './layers/AlcoholZoneLayer';
import RadiusRingLayer from './layers/RadiusRingLayer';
import SensorConnectionLayer from './layers/SensorConnectionLayer';
import CompetitorHeatmapLayer from './layers/CompetitorHeatmapLayer';
import NightLightsLayer from './layers/NightLightsLayer';

// Controls
import LayerTogglePanel from './controls/LayerTogglePanel';
import MapLegend from './controls/MapLegend';
import SubjectPropertyCard from './controls/SubjectPropertyCard';

const defaultContainerStyle = {
  width: '100%',
  height: 'calc(100vh - 220px)',
  minHeight: 500,
  borderRadius: 12,
  overflow: 'hidden',
};

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

/** Must be stable ref — never recreate this array */
const LIBRARIES = ['places', 'visualization'];

const InvestmentMap = ({
  subjectCoords,          // { lat, lng }
  trafficSensors = [],    // array of sensor objects
  competitors = {},       // { gas_stations, convenience_stores }
  alcoholZones = {},      // { churches, schools }
  address = '',           // subject property address string
  /** Shorter map for modals / embedded panels */
  compact = false,
  /** Fill parent height (use inside flex column with min-h-0); map uses remaining space below stats bar */
  fillContainer = false,
}) => {
  /* --------------------------------------------------------- */
  /* Google Maps loader                                         */
  /* --------------------------------------------------------- */
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'lotintel-maps',
    libraries: LIBRARIES,
  });

  /* --------------------------------------------------------- */
  /* Layer visibility state                                     */
  /* --------------------------------------------------------- */
  const [layers, setLayers] = useState({
    trafficSensors: true,
    sensorLines: true,
    gasStations: true,
    cStores: true,
    heatmap: false,
    alcoholZones: true,
    radiusRings: true,
    nightLights: false,
    propertyCard: true,
  });
  const [nightLightsOpacity, setNightLightsOpacity] = useState(0.6);

  /* --------------------------------------------------------- */
  /* Map instance ref (needed for heatmap + programmatic ops)    */
  /* --------------------------------------------------------- */
  const mapRef = useRef(null);
  const [showSubjectInfo, setShowSubjectInfo] = useState(false);
  const onLoad = useCallback((map) => { mapRef.current = map; }, []);
  const onUnmount = useCallback(() => { mapRef.current = null; }, []);

  /* --------------------------------------------------------- */
  /* Derive a safe centre                                       */
  /* --------------------------------------------------------- */
  const centre = useMemo(() => {
    if (subjectCoords?.lat && subjectCoords?.lng) {
      return { lat: Number(subjectCoords.lat), lng: Number(subjectCoords.lng) };
    }
    // Fallback: first traffic sensor that has coords
    for (const s of trafficSensors) {
      const lat = s.location?.lat ?? s.latitude;
      const lon = s.location?.lon ?? s.location?.lng ?? s.longitude;
      if (lat != null && lon != null) return { lat: Number(lat), lng: Number(lon) };
    }
    return { lat: 33.749, lng: -84.388 }; // Atlanta default
  }, [subjectCoords, trafficSensors]);

  /* --------------------------------------------------------- */
  /* Map options                                                */
  /* --------------------------------------------------------- */
  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
    gestureHandling: 'greedy',
  }), []);

  const containerStyle = useMemo(() => {
    if (fillContainer) {
      return {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        minHeight: 200,
        borderRadius: 0,
        overflow: 'hidden',
      };
    }
    if (compact) {
      return {
        width: '100%',
        height: 'min(52vh, 520px)',
        minHeight: 320,
        borderRadius: 12,
        overflow: 'hidden',
      };
    }
    return defaultContainerStyle;
  }, [compact, fillContainer]);

  /* --------------------------------------------------------- */
  /* Error / Loading states                                     */
  /* --------------------------------------------------------- */
  if (loadError) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>
        <h3>Failed to load Google Maps</h3>
        <p style={{ color: '#6b7280', marginTop: 8 }}>
          {loadError.message || 'Please check your API key and network connection.'}
        </p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🗺️</div>
        Loading Investment Map…
      </div>
    );
  }

  /* --------------------------------------------------------- */
  /* Stats bar                                                  */
  /* --------------------------------------------------------- */
  const sensorCount = trafficSensors.length;
  const gasCount = (() => {
    const g = competitors.gas_stations;
    if (Array.isArray(g)) return g.length;
    if (g && typeof g === 'object') return Object.values(g).flat().length;
    return 0;
  })();
  const csCount = (() => {
    const c = competitors.convenience_stores;
    if (Array.isArray(c)) return c.length;
    if (c && typeof c === 'object') return Object.values(c).flat().length;
    return 0;
  })();
  const churchCount = (alcoholZones.churches || []).length;
  const schoolCount = (alcoholZones.schools || []).length;

  /** Nearest sensor (for property card) */
  const nearestSensor = trafficSensors.length > 0
    ? [...trafficSensors].sort((a, b) => (a.distance_meters || Infinity) - (b.distance_meters || Infinity))[0]
    : null;

  /* --------------------------------------------------------- */
  /* Render                                                     */
  /* --------------------------------------------------------- */
  const rootStyle = fillContainer
    ? {
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }
    : { position: 'relative' };

  const mapShellStyle = fillContainer
    ? {
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        position: 'relative',
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      };

  return (
    <div style={rootStyle}>
      {/* Quick stats bar */}
      <div style={{
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        marginBottom: fillContainer ? 8 : 12,
        flexShrink: 0,
        fontSize: fillContainer ? 12 : 13,
        color: '#4b5563',
        fontFamily: 'system-ui, sans-serif',
      }}>
        <span>🚦 <b>{sensorCount}</b> sensors</span>
        <span>⛽ <b>{gasCount}</b> gas stations</span>
        <span>🏪 <b>{csCount}</b> c-stores</span>
        <span>⛪ <b>{churchCount}</b> churches</span>
        <span>🏫 <b>{schoolCount}</b> schools</span>
        <span style={{ marginLeft: 'auto', color: '#9ca3af' }}>
          📍 {centre.lat.toFixed(5)}, {centre.lng.toFixed(5)}
        </span>
      </div>

      {/* Map container */}
      <div style={mapShellStyle}>
        <div
          style={
            fillContainer
              ? { flex: 1, minHeight: 0, position: 'relative', width: '100%' }
              : { position: 'relative' }
          }
        >
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={centre}
            zoom={15}
            options={mapOptions}
            onLoad={onLoad}
            onUnmount={onUnmount}
          >
          {/* ===== Subject property marker — big, bright, unmissable ===== */}
          <Marker
            position={centre}
            onClick={() => setShowSubjectInfo(true)}
            icon={{
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="22" fill="#3b82f6" stroke="#fff" stroke-width="4"/>
                  <circle cx="24" cy="24" r="10" fill="#fff"/>
                  <circle cx="24" cy="24" r="5" fill="#1d4ed8"/>
                </svg>
              `),
              scaledSize: { width: 48, height: 48 },
              anchor: { x: 24, y: 24 },
            }}
            title="Subject Property"
            zIndex={200}
            animation={window.google?.maps?.Animation?.DROP}
          />
          {showSubjectInfo && (
            <InfoWindow position={centre} onCloseClick={() => setShowSubjectInfo(false)}>
              <div style={{ minWidth: 200, fontFamily: 'system-ui', color: '#1f2937' }}>
                <h4 style={{ margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#1e40af', borderBottom: '2px solid #3b82f6', paddingBottom: 4 }}>
                  📍 Subject Property
                </h4>
                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 600 }}>{address || 'Address'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {centre.lat.toFixed(6)}, {centre.lng.toFixed(6)}
                  </div>
                  {nearestSensor && (
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                      <b>Nearest road:</b> {nearestSensor.road_type || nearestSensor.station_id}<br/>
                      <b>AADT:</b> {nearestSensor.latest_aadt?.toLocaleString()}<br/>
                      <b>Distance:</b> {Math.round(nearestSensor.distance_meters || 0)} m
                    </div>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}

          {/* ===== Sensor connection lines ===== */}
          {layers.sensorLines && (
            <SensorConnectionLayer center={centre} sensors={trafficSensors} />
          )}

          {/* ===== Traffic sensors ===== */}
          {layers.trafficSensors && (
            <TrafficSensorLayer sensors={trafficSensors} />
          )}

          {/* ===== Competitors ===== */}
          <CompetitorLayer
            gasStations={competitors.gas_stations}
            cStores={competitors.convenience_stores}
            showGas={layers.gasStations}
            showCStores={layers.cStores}
          />

          {/* ===== Competitor heatmap ===== */}
          {layers.heatmap && (
            <CompetitorHeatmapLayer
              map={mapRef.current}
              gasStations={competitors.gas_stations}
              cStores={competitors.convenience_stores}
            />
          )}

          {/* ===== Alcohol permit zones ===== */}
          {layers.alcoholZones && (
            <AlcoholZoneLayer
              churches={alcoholZones.churches || []}
              schools={alcoholZones.schools || []}
            />
          )}

          {/* ===== Radius rings ===== */}
          {layers.radiusRings && (
            <RadiusRingLayer center={centre} />
          )}

          {/* ===== Night Lights overlay ===== */}
          {layers.nightLights && (
            <NightLightsLayer
              map={mapRef.current}
              opacity={nightLightsOpacity}
              visible={layers.nightLights}
            />
          )}
        </GoogleMap>
        </div>

        {/* Overlay controls */}
        {layers.propertyCard && (
          <SubjectPropertyCard
            address={address}
            coords={centre}
            sensorCount={sensorCount}
            competitorCount={gasCount + csCount}
            nearestSensor={nearestSensor}
          />
        )}
        <LayerTogglePanel
          layers={layers}
          onChange={setLayers}
          nightLightsOpacity={nightLightsOpacity}
          onOpacityChange={setNightLightsOpacity}
        />
        <MapLegend showNightLights={layers.nightLights} />
      </div>
    </div>
  );
};

export default InvestmentMap;
